
import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import { ActionCards } from "@/components/dashboard/ActionCards";
import { FloorPlansList } from "@/components/dashboard/FloorPlansList";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { FloorPlanFilters } from "@/components/dashboard/FloorPlanFilters";
import { useFloorPlans } from "@/hooks/useFloorPlans";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Trash2, X, Info } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";

interface Folder {
  id: string;
  name: string;
}

export default function Dashboard() {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [showDeprecationNotice, setShowDeprecationNotice] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const {
    floorPlans,
    loading,
    editingName,
    setEditingName,
    handleUpdateName,
    handleDuplicate,
    handleDelete,
    handleUpdateFolder,
    handleToggleFavorite,
    handleToggleVisibility,
  } = useFloorPlans();

  const hasCurrentPlan = useMemo(() => {
    return floorPlans.some(plan => plan.is_active === true);
  }, [floorPlans]);

  useEffect(() => {
    if (user) {
      fetchFolders();
    }
  }, [user]);

  const fetchFolders = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('folders')
        .select('*')
        .eq('user_id', user.id)
        .order('name');

      if (error) throw error;
      setFolders(data || []);
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  };

  const filteredFloorPlans = useMemo(() => {
    return floorPlans.filter(plan => {
      const matchesSearch = plan.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFolder = selectedFolder === "all" || !selectedFolder
        ? true
        : plan.folder_id === selectedFolder;
      const matchesFavorites = showFavorites ? plan.is_favorite : true;
      return matchesSearch && matchesFolder && matchesFavorites;
    });
  }, [floorPlans, searchQuery, selectedFolder, showFavorites]);

  const handleSelectPlan = (planId: string) => {
    setSelectedPlans(prev => {
      if (prev.includes(planId)) {
        return prev.filter(id => id !== planId);
      } else {
        return [...prev, planId];
      }
    });
  };

  const handleDeleteSelected = async () => {
    try {
      for (const planId of selectedPlans) {
        await handleDelete(planId);
      }
      setSelectedPlans([]);
      setDeleteDialogOpen(false);
      toast({
        title: "Plattegronden verwijderd",
        description: `${selectedPlans.length} plattegrond(en) zijn succesvol verwijderd.`,
      });
    } catch (error) {
      console.error('Error deleting floor plans:', error);
      toast({
        title: "Fout bij verwijderen",
        description: "Er is een fout opgetreden bij het verwijderen van de plattegronden.",
        variant: "destructive",
      });
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-6 py-8">
        {/* Deprecation Notice */}
        {showDeprecationNotice && (
          <Alert className="mb-6 border-orange-200 bg-orange-50">
            <Info className="h-4 w-4 text-orange-600" />
            <AlertDescription className="flex items-center justify-between">
              <div className="flex-1">
                <span className="text-orange-800">
                  Deze pagina wordt niet meer gebruikt. Je kunt je plattegronden nu vinden via de "Openen" knop op de hoofdpagina.
                </span>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/')}
                  className="text-orange-800 border-orange-300 hover:bg-orange-100"
                >
                  Ga naar hoofdpagina
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeprecationNotice(false)}
                  className="text-orange-600 hover:bg-orange-100 p-1"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <DashboardHeader />
        
        <div className="mt-8 space-y-8">
          <ActionCards hasCurrentPlan={hasCurrentPlan} />
          
          <div className="flex items-center justify-between">
            <FloorPlanFilters
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              selectedFolder={selectedFolder}
              setSelectedFolder={setSelectedFolder}
              folders={folders}
              showFavorites={showFavorites}
              onToggleFavorites={setShowFavorites}
            />
            {selectedPlans.length > 0 && (
              <Button
                variant="destructive"
                onClick={() => setDeleteDialogOpen(true)}
                className="ml-4"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Verwijder {selectedPlans.length} geselecteerd
              </Button>
            )}
          </div>

          <FloorPlansList
            loading={loading}
            floorPlans={filteredFloorPlans}
            editingName={editingName}
            onEditName={setEditingName}
            onUpdateName={handleUpdateName}
            onDuplicate={handleDuplicate}
            onDelete={handleDelete}
            onUpdateFolder={handleUpdateFolder}
            onToggleFavorite={handleToggleFavorite}
            onToggleVisibility={handleToggleVisibility}
            selectedPlans={selectedPlans}
            onSelectPlan={handleSelectPlan}
          />
        </div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Plattegronden verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je {selectedPlans.length} plattegrond(en) wilt verwijderen? 
              Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSelected}>
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
